import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useWorkflow } from '../../context/WorkflowContext';
import { BookOpen, ChevronLeft, ChevronRight, Target, Key, Lightbulb, FileText, CheckCircle, Edit3, RefreshCw, Home } from 'lucide-react';
import StoryScene from '../3D/StoryScene';
import FeedbackModal from '../FeedbackModal';
import { extractRevisedContent } from '../../utils/contentParser';
import ReactMarkdown from 'react-markdown';

const StageContainer = styled.div`
  width: 100vw;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const SceneContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 1;
`;

const BookContainer = styled(motion.div)`
  width: 90%;
  max-width: 1200px;
  height: 85vh;
  background: #000;
  border-radius: 20px;
  position: relative;
  z-index: 10;
  margin: 2rem;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  overflow: visible;
  
  @media (max-width: 1024px) {
    width: 95%;
    height: 80vh;
    margin: 1.5rem;
  }
  
  @media (max-width: 768px) {
    width: 98%;
    height: 75vh;
    margin: 1rem;
    border-radius: 15px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    height: 70vh;
    margin: 0.5rem;
    border-radius: 10px;
  }
`;

const BookPages = styled.div`
  flex: 1;
  position: relative;
  background: #1a1a1a;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SinglePage = styled(motion.div)`
  width: 92%;
  max-width: 900px;
  height: 65vh;
  max-height: 550px;
  padding: 3rem;
  display: flex;
  flex-direction: column;
  position: relative;
  background: #2d2d2d;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(74, 144, 226, 0.6);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(74, 144, 226, 0.8);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(255, 255, 255, 0.03) 2px,
      rgba(255, 255, 255, 0.03) 4px
    );
    pointer-events: none;
    border-radius: 15px;
  }
  
  @media (max-width: 1024px) {
    width: 95%;
    height: 60vh;
    max-height: 500px;
    padding: 2.5rem;
  }
  
  @media (max-width: 768px) {
    width: 98%;
    height: 55vh;
    max-height: 450px;
    padding: 2rem;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    height: 50vh;
    max-height: 400px;
    padding: 1.5rem;
    border-radius: 8px;
  }
`;

const PageContent = styled.div`
  color: #fff;
  line-height: 1.8;
  font-size: 1.1rem;
  z-index: 2;
  position: relative;
  flex: 1;
  min-height: 0;
  
  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 2rem;
    color: #fff;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }
  
  h2 {
    font-size: 2rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    color: #4A90E2;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }
  
  h3 {
    font-size: 1.5rem;
    font-weight: 500;
    margin-bottom: 1rem;
    color: #66B3FF;
  }
  
  p {
    margin-bottom: 1.5rem;
    color: #e0e0e0;
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
  }
  
  li {
    margin-bottom: 0.5rem;
    color: #e0e0e0;
  }
  
  .quote {
    font-style: italic;
    font-size: 1.3rem;
    text-align: center;
    color: #fff;
    margin: 2rem 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }
  
  .instructions {
    font-size: 0.9rem;
    color: #999;
    text-align: center;
    margin-top: 2rem;
  }
  
  @media (max-width: 1024px) {
    font-size: 1rem;
    
    h1 {
      font-size: 2rem;
      margin-bottom: 1.5rem;
    }
    
    h2 {
      font-size: 1.7rem;
      margin-bottom: 1.2rem;
    }
    
    h3 {
      font-size: 1.3rem;
    }
    
    .quote {
      font-size: 1.1rem;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.6;
    
    h1 {
      font-size: 1.7rem;
      margin-bottom: 1.2rem;
    }
    
    h2 {
      font-size: 1.5rem;
      margin-bottom: 1rem;
    }
    
    h3 {
      font-size: 1.2rem;
    }
    
    p {
      margin-bottom: 1.2rem;
    }
    
    ul, ol {
      padding-left: 1.5rem;
      margin-bottom: 1.2rem;
    }
    
    .quote {
      font-size: 1rem;
      margin: 1.5rem 0;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    line-height: 1.5;
    
    h1 {
      font-size: 1.4rem;
      margin-bottom: 1rem;
    }
    
    h2 {
      font-size: 1.2rem;
      margin-bottom: 0.8rem;
    }
    
    h3 {
      font-size: 1rem;
    }
    
    p {
      margin-bottom: 1rem;
    }
    
    ul, ol {
      padding-left: 1.2rem;
      margin-bottom: 1rem;
    }
    
    .quote {
      font-size: 0.9rem;
      margin: 1rem 0;
    }
    
    .instructions {
      font-size: 0.8rem;
    }
  }
`;

const NavigationBar = styled.div`
  min-height: 80px;
  background: #111;
  border-top: 1px solid #333;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 2rem;
  z-index: 10;
  flex-shrink: 0;
  position: relative;
  
  @media (max-width: 768px) {
    min-height: 70px;
    padding: 0.8rem 1.5rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    min-height: 60px;
    padding: 0.5rem 1rem;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const NavButton = styled(motion.button)`
  background: ${props => props.$active ? '#fff' : '#4A90E2'};
  color: ${props => props.$active ? '#000' : '#fff'};
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: all 0.3s ease;
  min-height: 50px;
  
  &:hover {
    background: ${props => props.$active ? '#f0f0f0' : '#3b82f6'};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
    min-height: 45px;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    min-height: 40px;
    width: 100%;
    justify-content: center;
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 4px;
  background: #333;
  border-radius: 2px;
  margin: 0 2rem;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    margin: 0 1rem;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    margin: 0.5rem 0;
    order: -1;
  }
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #4A90E2 0%, #66B3FF 100%);
  border-radius: 2px;
  position: absolute;
  left: 0;
  top: 0;
`;

const PageIndicator = styled.div`
  color: #999;
  font-size: 0.9rem;
  font-weight: 500;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem 2rem;
  background: #111;
  border-top: 1px solid #333;
  justify-content: center;
  z-index: 10;
  flex-shrink: 0;
  position: relative;
  min-height: 100px;
`;

const Button = styled(motion.button)`
  min-width: 200px;
  min-height: 60px;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 15;

  &.approve {
    background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
    color: white;
  }

  &.request-changes {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    color: white;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #fff;
  text-align: center;
`;

const LoadingSpinner = styled(motion.div)`
  width: 50px;
  height: 50px;
  border: 3px solid #333;
  border-top: 3px solid #4A90E2;
  border-radius: 50%;
  margin-bottom: 1rem;
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: rgba(74, 144, 226, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  z-index: 10;
  pointer-events: none;
  backdrop-filter: blur(10px);
`;

const RefreshButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(74, 222, 128, 0.4);
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(74, 222, 128, 0.5);
  }

  &:active {
    transform: translateY(0);
  }
`;

const StoryStage = () => {
  const navigate = useNavigate();
  const { 
    storyContent, 
    sessionId, 
    isProcessing, 
    approveStage, 
    requestChanges,
    setFeedbackText,
    feedbackText,
    isRequestingChanges,
    isApproving,
    setRequestingChanges,
    setApproving,
    reloadContent
  } = useWorkflow();
  
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [showRefreshButton, setShowRefreshButton] = useState(() => {
    return localStorage.getItem('showRefreshButton_story') === 'true';
  });

  // Ensure button appears when request completes
  useEffect(() => {
    if (!isRequestingChanges && localStorage.getItem('showRefreshButton_story') === 'true') {
      setShowRefreshButton(true);
    }
  }, [isRequestingChanges]);

  // Parse story content into different sections
  const parseStoryContent = (content) => {
    if (!content) return null;
    
    
    // First, extract revised content if it exists
    const revisedContent = extractRevisedContent(content, 'story');
    
    const pages = [];
    
    // Simple and reliable parsing approach
    const lines = revisedContent.split('\n');
    let currentSection = null;
    let currentContent = [];
    const sections = {};
    
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check if this line is a section header (must be short and not numbered)
      if ((line.toLowerCase() === 'story:' || line === 'Story:') && 
          line.length < 20 && !/^\d+\./.test(line)) {
        // Save previous section
        if (currentSection && currentContent.length > 0) {
          sections[currentSection] = currentContent.join('\n').trim();
        }
        currentSection = 'story';
        currentContent = [];
      } else if ((line.toLowerCase() === 'learning outcomes:' || line === 'Learning Outcomes:' || 
                 line.toLowerCase() === 'learning outcomes.' || line === 'Learning Outcomes.' ||
                 line.toLowerCase() === 'learning outcomes' || line === 'Learning Outcomes') && 
                 line.length < 30 && !/^\d+\./.test(line)) {
        // Save previous section
        if (currentSection && currentContent.length > 0) {
          sections[currentSection] = currentContent.join('\n').trim();
        }
        currentSection = 'outcomes';
        currentContent = [];
        
        // Check if this is revised content
        if (revisedContent.includes('REVISED') || revisedContent.includes('UPDATED')) {
        }
      } else if ((line.toLowerCase().trim() === 'keywords:' || line.trim() === 'Keywords:') && 
                 line.length < 20 && !/^\d+\./.test(line)) {
        // Save previous section - only switch if this is a clear section header (not part of content)
        if (currentSection && currentContent.length > 0) {
          sections[currentSection] = currentContent.join('\n').trim();
        }
        currentSection = 'keywords';
        currentContent = [];
      } else if ((line.toLowerCase().trim() === 'concepts:' || line.trim() === 'Concepts:') && 
                 line.length < 20 && !/^\d+\./.test(line)) {
        // Save previous section - only switch if this is a clear section header (not part of content)
        if (currentSection && currentContent.length > 0) {
          sections[currentSection] = currentContent.join('\n').trim();
        }
        currentSection = 'concepts';
        currentContent = [];
      } else if (currentSection && line) {
        // Add content to current section (skip empty lines but allow all other content)
        // Only skip lines that are just "&" or empty
        if (line !== '&' && line.trim() !== '') {
          currentContent.push(line);
        }
        
        // Debug content collection for all sections
        if (currentSection === 'outcomes') {
          
          // Special debugging for learning outcomes
          if (line.includes('REVISED') || line.includes('UPDATED')) {
          }
        } else if (currentSection === 'keywords') {
        } else if (currentSection === 'concepts') {
          
          // Special debugging for concepts
          if (line.includes('REVISED') || line.includes('UPDATED')) {
          }
          
          // Debug numbered items in concepts
          const isNumberedList = /^\d+\./.test(line);
          if (isNumberedList) {
          }
          
          // Debug if line contains section header keywords but isn't a section header
          if (line.toLowerCase().includes('learning outcomes') || 
              line.toLowerCase().includes('keywords') || 
              line.toLowerCase().includes('concepts') ||
              line.toLowerCase().includes('story:')) {
          }
        }
      }
    }
    
    // Save the last section
    if (currentSection && currentContent.length > 0) {
      sections[currentSection] = currentContent.join('\n').trim();
      
      // Debug: Show what we collected
      Object.entries(sections).forEach(([name, content]) => {
        const lines = content.split('\n').filter(line => line.trim());
        const numberedItems = lines.filter(line => /^\d+\./.test(line.trim()));
        
        // Special debug for outcomes
        if (name === 'outcomes') {
          numberedItems.forEach((item, idx) => {
          });
        }
      });
      
      // Debug final content for all sections
      if (currentSection === 'outcomes') {
        const outcomeLines = sections[currentSection].split('\n').filter(line => line.trim());
        const numberedOutcomes = outcomeLines.filter(line => /^\d+\./.test(line.trim()));
        numberedOutcomes.forEach((outcome, idx) => {
        });
      } else if (currentSection === 'keywords') {
      } else if (currentSection === 'concepts') {
      }
    }
    
    // Post-process to clean up only empty items, not valid content
    if (sections.concepts) {
      const conceptsLines = sections.concepts.split('\n');
      const cleanedConceptsLines = conceptsLines.filter(line => {
        const lineTrimmed = line.trim();
        const isNumberedList = /^\d+\./.test(lineTrimmed);
        
        // Only remove truly empty numbered items (like "6." with no content)
        if (isNumberedList && lineTrimmed.length <= 3) {
          return false;
        }
        
        // Keep all other content, including numbered items with actual content
        return true;
      });
      sections.concepts = cleanedConceptsLines.join('\n').trim();
    }
    
    // Also clean up keywords section for empty items only
    if (sections.keywords) {
      const keywordsLines = sections.keywords.split('\n');
      const cleanedKeywordsLines = keywordsLines.filter(line => {
        const lineTrimmed = line.trim();
        const isNumberedList = /^\d+\./.test(lineTrimmed);
        
        // Only remove truly empty numbered items (like "18." with no content)
        if (isNumberedList && lineTrimmed.length <= 3) {
          return false;
        }
        
        // Keep all other content, including numbered items with actual content
        return true;
      });
      sections.keywords = cleanedKeywordsLines.join('\n').trim();
    }
    
    // No content mixing - keep sections separate

    // Extract content for each section
    const extractContent = (sectionName) => {
      const content = sections[sectionName] || '';
      return content;
    };
    
    // Page 1: Summary
    const summaryContent = extractContent('story');
    pages.push({
      type: 'summary',
      title: 'Summary',
      icon: FileText,
      content: summaryContent || 
               'This educational story explores key concepts through an engaging narrative that helps students understand complex topics in an accessible way.'
    });
    
    // Page 2: Learning Outcomes
    const outcomesContent = extractContent('outcomes');

    pages.push({
      type: 'outcomes',
      title: 'Learning Outcomes',
      icon: Target,
      content: outcomesContent || 
               'By the end of this story, students will be able to:\n\n• Understand the main concepts presented in the narrative\n• Apply knowledge to real-world scenarios and situations\n• Develop critical thinking and analytical skills\n• Enhance their learning experience through interactive engagement'
    });
    
    // Page 3: Keywords
    let keywordsContent = extractContent('keywords');
    
    // Page 4: Concepts
    let conceptsContent = extractContent('concepts');
    
    // No content mixing - keep sections separate
    
    pages.push({
      type: 'keywords',
      title: 'Keywords',
      icon: Key,
      content: keywordsContent ||
               'Key terms and vocabulary covered in this story:\n\n• Educational Technology\n• Interactive Learning\n• Student Engagement\n• Knowledge Application\n• Critical Thinking'
    });
    
    pages.push({
      type: 'concepts',
      title: 'Concepts',
      icon: Lightbulb,
      content: conceptsContent ||
               'Core concepts explored in this educational story:\n\n• **Learning Through Narrative**: How stories make complex topics accessible\n• **Interactive Education**: Engaging students through interactive content\n• **Knowledge Transfer**: From theoretical to practical application\n• **Critical Analysis**: Developing evaluation and analysis skills'
    });
    
    return pages;
  };


  const storyPages = parseStoryContent(storyContent);
  
  const totalPages = storyPages ? storyPages.length : 0;

  // Auto-reload content if missing
  useEffect(() => {
    if (sessionId && !storyContent && !isProcessing) {
      reloadContent();
    }
  }, [sessionId, storyContent, isProcessing, reloadContent]);

  // Reset component state when storyContent changes
  useEffect(() => {
    if (storyContent) {
      
      // Parse content to see if it's valid
      const parsedContent = parseStoryContent(storyContent);
      
      setRequestingChanges(false);
      setApproving(false);
      setCurrentPage(0); // Reset to first page when content loads
      
      // Hide refresh button when content is updated
      setShowRefreshButton(false);
    }
  }, [storyContent, setRequestingChanges, setApproving]);

  // Check if content needs scrolling
  useEffect(() => {
    const checkScrollable = () => {
      const pageElement = document.querySelector('[data-page-content]');
      if (pageElement) {
        const isScrollable = pageElement.scrollHeight > pageElement.clientHeight;
        setShowScrollIndicator(isScrollable);
      }
    };

    // Check after content loads
    const timeoutId = setTimeout(checkScrollable, 100);
    
    // Check on window resize
    window.addEventListener('resize', checkScrollable);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkScrollable);
    };
  }, [currentPage, storyContent]);

  const handleApprove = async () => {
    if (!sessionId) return;
    
    try {
      setApproving(true);
      await approveStage(sessionId);
      navigate('/dialogues');
    } catch (error) {
      console.error('Failed to approve story:', error);
    } finally {
      setApproving(false);
    }
  };

  const handleRequestChanges = () => {
    if (isApproving) return;
    setShowModal(true);
  };

  const handleSubmitFeedback = async (feedback = null) => {
    
    const feedbackToSubmit = feedback || feedbackText;
    
    if (!sessionId) {
      return;
    }
    
    if (!feedbackToSubmit?.trim()) {
      return;
    }
    
    
    try {
      
      // Call the context function (which handles the API call)
      await requestChanges(sessionId, 'story', feedbackToSubmit);
      
      // Show refresh button after changes are received - use both state and localStorage
      setShowRefreshButton(true);
      localStorage.setItem('showRefreshButton_story', 'true');
      setShowModal(false);
      alert('Changes received. Please press the Reload Content Button.');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  const handleRefresh = () => {
    localStorage.removeItem('showRefreshButton_story');
    window.location.reload();
  };

  const nextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));
  };

  const prevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
  };

  // Show proper message when no session is started
  if (!sessionId) {
    return (
      <StageContainer>
        <SceneContainer>
          <StoryScene storyContent={null} />
        </SceneContainer>
        
        <BookContainer
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
        <BookPages>
          <SinglePage>
            <PageContent>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
                <BookOpen size={32} color="#4A90E2" />
                <h1>Educational Story</h1>
              </div>
              <p>Welcome to the interactive learning experience!</p>
              <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                <div className="quote">"Stories are doors; keys are choices."</div>
                <div className="instructions">Use navigation to explore different sections</div>
              </div>
            </PageContent>
          </SinglePage>
        </BookPages>
          
          <NavigationBar>
            <NavButton disabled>
              <ChevronLeft size={20} />
              Previous
            </NavButton>
            <ProgressBar>
              <ProgressFill style={{ width: '0%' }} />
            </ProgressBar>
            <NavButton disabled>
              Next
              <ChevronRight size={20} />
            </NavButton>
          </NavigationBar>
        </BookContainer>
      </StageContainer>
    );
  }


  if (!storyContent) {
    return (
      <StageContainer>
        <SceneContainer>
          <StoryScene storyContent={null} />
        </SceneContainer>
        
        <BookContainer
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <LoadingContainer>
            <LoadingSpinner
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
            <h2>Loading story content...</h2>
            <p>Please wait while we prepare your educational story</p>
            <Button
              className="reload"
              onClick={() => window.location.reload()}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                marginTop: '2rem',
                background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
                color: 'white',
                minWidth: '200px',
                minHeight: '50px',
                padding: '1rem 1.5rem',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
              }}
            >
              <RefreshCw size={20} />
              Reload Content
            </Button>
          </LoadingContainer>
        </BookContainer>
      </StageContainer>
    );
  }

  const currentPageData = storyPages[currentPage];
  const IconComponent = currentPageData?.icon || BookOpen;
  

  return (
    <StageContainer>
      <SceneContainer>
        <StoryScene storyContent={storyContent} />
      </SceneContainer>
      
      {/* Refresh Button - appears after changes are received */}
      {(showRefreshButton || localStorage.getItem('showRefreshButton_story') === 'true') && (
        <RefreshButton onClick={handleRefresh}>
          <RefreshCw size={16} />
          Refresh
        </RefreshButton>
      )}
      
      <BookContainer
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <BookPages>
          <SinglePage
            key={`page-${currentPage}`}
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -50, opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-page-content
          >
            <PageContent>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
                <IconComponent size={32} color="#4A90E2" />
                <h1>{currentPageData?.title}</h1>
              </div>
              
              
              {currentPageData?.type === 'outcomes' ? (
                <div>
                  <h3>Learning Outcomes:</h3>
                  <div style={{ marginTop: '1rem' }}>
                    {currentPageData.content ? (
                      <div>
                        {/* Try to parse numbered outcomes first */}
                        {(() => {
                          const allLines = currentPageData.content.split('\n');
                          const numberedOutcomes = allLines.filter(line => {
                            const trimmed = line.trim();
                            return trimmed && /^\d+\./.test(trimmed); // Keep all numbered items with content
                          });
                          
                          
                          return numberedOutcomes.map((outcome, index) => (
                            <div key={index} style={{ marginBottom: '1rem', paddingLeft: '1rem' }}>
                              <p style={{ margin: 0, lineHeight: '1.6' }}>{outcome.trim()}</p>
                            </div>
                          ));
                        })()}
                        
                        {/* If no numbered outcomes, try bullet points */}
                        {currentPageData.content
                          .split('\n')
                          .filter(line => line.trim() && line.match(/^\d+\./)).length === 0 &&
                          currentPageData.content
                            .split('\n')
                            .filter(line => line.trim() && line.match(/^[•\-\*]/))
                            .map((outcome, index) => (
                              <div key={index} style={{ marginBottom: '1rem', paddingLeft: '1rem' }}>
                                <p style={{ margin: 0, lineHeight: '1.6' }}>{outcome.trim()}</p>
                              </div>
                            ))}
                        
                        {/* If no structured content, show the raw content */}
                        {currentPageData.content
                          .split('\n')
                          .filter(line => line.trim() && (line.match(/^\d+\./) || line.match(/^[•\-\*]/))).length === 0 && (
                          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                            {currentPageData.content}
                          </div>
                        )}
                      </div>
                    ) : (
                      <ul style={{ marginTop: '1rem', paddingLeft: '2rem' }}>
                        <li>Understand the main concepts presented in the narrative</li>
                        <li>Apply knowledge to real-world scenarios and situations</li>
                        <li>Develop critical thinking and analytical skills</li>
                        <li>Enhance their learning experience through interactive engagement</li>
                      </ul>
                    )}
                  </div>
                </div>
              ) : currentPageData?.type === 'keywords' ? (
                <div>
                  <h3>Keywords:</h3>
                  <div style={{ marginTop: '1rem' }}>
                    {currentPageData.content ? (
                      <div>
                        {/* Try to parse numbered lists first */}
                        {currentPageData.content
                          .split('\n')
                          .filter(line => {
                            const trimmed = line.trim();
                            return trimmed && line.match(/^\d+\./); // Keep all numbered items with content
                          })
                          .map((keyword, index) => (
                            <div key={index} style={{ marginBottom: '0.5rem', paddingLeft: '1rem' }}>
                              <p style={{ margin: 0, lineHeight: '1.6' }}>{keyword.trim()}</p>
                            </div>
                          ))}
                        
                        {/* If no numbered lists, try bullet points */}
                        {currentPageData.content
                          .split('\n')
                          .filter(line => line.trim() && line.match(/^\d+\./)).length === 0 &&
                          currentPageData.content
                          .split('\n')
                          .filter(line => line.trim() && line.match(/^[•\-\*]/))
                          .map((keyword, index) => (
                            <div key={index} style={{ marginBottom: '0.5rem', paddingLeft: '1rem' }}>
                              <p style={{ margin: 0, lineHeight: '1.6' }}>{keyword.trim()}</p>
                            </div>
                          ))}
                        
                        {/* If no structured content, show the raw content */}
                        {currentPageData.content
                          .split('\n')
                          .filter(line => line.trim() && (line.match(/^\d+\./) || line.match(/^[•\-\*]/))).length === 0 && (
                          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                            {currentPageData.content}
                          </div>
                        )}
                      </div>
                    ) : (
                      <ul style={{ marginTop: '1rem', paddingLeft: '2rem' }}>
                        <li>Educational Technology</li>
                        <li>Interactive Learning</li>
                        <li>Student Engagement</li>
                        <li>Knowledge Application</li>
                        <li>Critical Thinking</li>
                      </ul>
                    )}
                  </div>
                </div>
              ) : currentPageData?.type === 'concepts' ? (
                <div>
                  <h3>Concepts:</h3>
                  <div style={{ marginTop: '1rem' }}>
                    {currentPageData.content ? (
                      <div>
                        {/* Force numbered list display for concepts */}
                        {(() => {
                          const lines = currentPageData.content.split('\n').filter(line => line.trim());
                          
                          // Try to find numbered items first (support both "1. text" and "1. [Name]: text" formats)
                          const numberedItems = lines.filter(line => {
                            const trimmed = line.trim();
                            return line.match(/^\d+\./) && trimmed.length > 0; // Keep all numbered items with any content
                          });
                          if (numberedItems.length > 0) {
                            return (
                              <ol style={{ marginTop: '1rem', paddingLeft: '2rem' }}>
                                {numberedItems.map((concept, index) => {
                                  // Remove the number prefix and clean up the text
                                  let cleanConcept = concept.replace(/^\d+\.\s*/, '').trim();
                                  
                                  // If it has the format "Name: Description", format it nicely
                                  if (cleanConcept.includes(':') && cleanConcept.split(':').length === 2) {
                                    const [name, description] = cleanConcept.split(':');
                                    return (
                                      <li key={index} style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
                                        <strong>{name.trim()}</strong>: {description.trim()}
                                      </li>
                                    );
                                  } else {
                                    return (
                                      <li key={index} style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
                                        {cleanConcept}
                                      </li>
                                    );
                                  }
                                })}
                              </ol>
                            );
                          }
                          
                          // If no numbered items, try bullet points
                          const bulletItems = lines.filter(line => line.match(/^[•\-\*]/));
                          if (bulletItems.length > 0) {
                            return (
                              <ol style={{ marginTop: '1rem', paddingLeft: '2rem' }}>
                                {bulletItems.map((concept, index) => (
                                  <li key={index} style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
                                    {concept.replace(/^[•\-\*]\s*/, '').trim()}
                                  </li>
                                ))}
                              </ol>
                            );
                          }
                          
                          // If no structured items, create numbered list from all lines
                          const allItems = lines.filter(line => line.length > 10);
                          if (allItems.length > 0) {
                            return (
                              <ol style={{ marginTop: '1rem', paddingLeft: '2rem' }}>
                                {allItems.map((concept, index) => (
                                  <li key={index} style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
                                    {concept.trim()}
                                  </li>
                                ))}
                              </ol>
                            );
                          }
                          
                          // Fallback: show raw content
                          return (
                            <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                              {currentPageData.content}
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      <ul style={{ marginTop: '1rem', paddingLeft: '2rem' }}>
                        <li><strong>Learning Through Narrative</strong>: How stories make complex topics accessible</li>
                        <li><strong>Interactive Education</strong>: Engaging students through interactive content</li>
                        <li><strong>Knowledge Transfer</strong>: From theoretical to practical application</li>
                        <li><strong>Critical Analysis</strong>: Developing evaluation and analysis skills</li>
                      </ul>
                    )}
                  </div>
                </div>
              ) : (
                <ReactMarkdown>{currentPageData?.content}</ReactMarkdown>
              )}
              
              <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                {currentPage === 0 && (
                  <div className="instructions">Overview of the educational content</div>
                )}
                {currentPage === 1 && (
                  <div className="instructions">What you'll achieve through this story</div>
                )}
                {currentPage === 2 && (
                  <div className="instructions">Key terms to focus on during your reading</div>
                )}
                {currentPage === 3 && (
                  <div className="instructions">Core ideas explored in this story</div>
                )}
              </div>
            </PageContent>
            
            {showScrollIndicator && (
              <ScrollIndicator
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
              >
                Scroll to read more
              </ScrollIndicator>
            )}
          </SinglePage>
        </BookPages>
        
        <NavigationBar>
          <NavButton
            onClick={prevPage}
            disabled={currentPage === 0}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronLeft size={20} />
            Previous
          </NavButton>
          
          <ProgressBar>
            <ProgressFill 
              style={{ width: `${((currentPage + 1) / totalPages) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </ProgressBar>
          
          <PageIndicator>
            Page {currentPage + 1} of {totalPages}
          </PageIndicator>
          
          <NavButton
            onClick={nextPage}
            disabled={currentPage === totalPages - 1}
            $active={currentPage === totalPages - 1}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {currentPage === totalPages - 1 ? 'Finish' : 'Next'}
            <ChevronRight size={20} />
          </NavButton>
        </NavigationBar>
        
             <ActionButtons>
               <Button
                 className="home"
                 onClick={() => navigate('/')}
                 disabled={isProcessing}
                 whileHover={{ scale: 1.05 }}
                 whileTap={{ scale: 0.95 }}
               >
                 <Home size={20} />
                 Home
               </Button>
               
               <Button
                 className="approve"
                 onClick={handleApprove}
                 disabled={isProcessing || isApproving}
                 whileHover={{ scale: 1.05 }}
                 whileTap={{ scale: 0.95 }}
               >
                 <CheckCircle size={20} />
                 {isApproving ? 'Approving...' : 'Continue to dialogues'}
               </Button>
               
               <Button
                 className="request-changes"
                 onClick={handleRequestChanges}
                 disabled={isProcessing || isRequestingChanges}
                 whileHover={{ scale: 1.05 }}
                 whileTap={{ scale: 0.95 }}
               >
                 <Edit3 size={20} />
                 {isRequestingChanges ? 'Processing...' : 'Request Changes'}
               </Button>
             </ActionButtons>
      </BookContainer>

           {showModal && (
             <FeedbackModal
               isOpen={showModal}
               onClose={() => setShowModal(false)}
               onSubmit={handleSubmitFeedback}
               feedbackText={feedbackText}
               setFeedbackText={setFeedbackText}
               stage="story"
               isSubmitting={isRequestingChanges}
               setRequestingChanges={setRequestingChanges}
               reloadContent={reloadContent}
             />
           )}
    </StageContainer>
  );
};

export default StoryStage;